// src/components/HealthDataSection.jsx
// Comprehensive Medicare Blue Button health data display
import React, { useState, useEffect } from 'react';
import AddMedicationModal from './AddMedicationModal';
import {
  getBlueButtonStatus,
  getBlueButtonAuthUrl,
  disconnectBlueButton,
  syncBlueButtonData,
  getBlueButtonClaims,
  getBlueButtonChanges,
  getHealthDataSummary,
  getOutpatientClaims,
  getDMEClaims,
  getSNFStays,
  getHospiceEpisodes,
  getMedicalEvents,
  getClientProviders,
  getCostAnalytics,
  getCoveragePeriods,
  sendBlueButtonLinkEmail,
  sendBlueButtonLinkSMS,
  getPatientAccessCarriers,
  matchPatientAccessCarrier,
  sendCarrierLinkEmail,
  sendCarrierLinkSMS,
  getClientCarrierConnections,
  disconnectCarrier,
  triggerCarrierSync,
  reclassifyEncounters
} from '../api';

// Carrier logos and info for display
const CARRIER_INFO = {
  humana: { name: 'Humana', logo: '/logos/humana.png', color: 'bg-green-500' },
  aetna: { name: 'Aetna', logo: '/logos/aetna.png', color: 'bg-purple-600' },
  anthem: { name: 'Anthem', logo: '/logos/anthem.png', color: 'bg-blue-600' },
  unitedhealthcare: { name: 'UnitedHealthcare', logo: '/logos/uhc.png', color: 'bg-blue-700' },
  cigna: { name: 'Cigna', logo: '/logos/cigna.png', color: 'bg-orange-500' },
  wellcare: { name: 'Wellcare', logo: '/logos/wellcare.png', color: 'bg-blue-500' },
  centene: { name: 'Centene', logo: '/logos/centene.png', color: 'bg-red-600' },
  molina: { name: 'Molina', logo: '/logos/molina.png', color: 'bg-teal-600' },
  kaiser: { name: 'Kaiser Permanente', logo: '/logos/kaiser.png', color: 'bg-blue-800' },
  caresource: { name: 'CareSource', logo: '/logos/caresource.png', color: 'bg-green-600' },
  devoted: { name: 'Devoted Health', logo: '/logos/devoted.png', color: 'bg-red-500' },
  clover: { name: 'Clover Health', logo: '/logos/clover.png', color: 'bg-green-500' },
  oscar: { name: 'Oscar Health', logo: '/logos/oscar.png', color: 'bg-yellow-500' },
  bcbs: { name: 'Blue Cross Blue Shield', logo: '/logos/bcbs.png', color: 'bg-blue-600' },
};

// Get carrier info from carrier name/id
function getCarrierInfo(carrier) {
  if (!carrier) return null;
  const normalized = carrier.toLowerCase().replace(/[^a-z]/g, '');

  // Check for exact match first
  if (CARRIER_INFO[normalized]) return CARRIER_INFO[normalized];

  // Check for partial matches
  for (const [key, info] of Object.entries(CARRIER_INFO)) {
    if (normalized.includes(key) || key.includes(normalized)) {
      return info;
    }
  }

  // Check for BCBS variations
  if (normalized.includes('bluecross') || normalized.includes('bcbs') || normalized.includes('anthem')) {
    return CARRIER_INFO.bcbs;
  }

  // Default - use the carrier name as-is
  return { name: carrier, logo: null, color: 'bg-gray-500' };
}

// Tab component for section navigation
function Tab({ active, onClick, children, count }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
        active
          ? 'text-blue-600 border-blue-600 bg-blue-50'
          : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
      }`}
    >
      {children}
      {count !== undefined && count > 0 && (
        <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
          active ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
        }`}>
          {count}
        </span>
      )}
    </button>
  );
}

// Format currency from cents
function formatCurrency(cents) {
  if (cents === null || cents === undefined) return '-';
  return `$${(cents / 100).toFixed(2)}`;
}

// Format date
function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString();
}

// Section header component
function SectionHeader({ title, subtitle }) {
  return (
    <div className="mb-4">
      <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
    </div>
  );
}

// Empty state component
function EmptyState({ message }) {
  return (
    <div className="text-center py-8 text-gray-500 text-sm">
      <svg className="w-12 h-12 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      {message}
    </div>
  );
}

export default function HealthDataSection({ clientId, clientName, clientEmail, clientPhone, clientCarrier }) {
  // Blue Button status
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [carrierSyncing, setCarrierSyncing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  // Carrier connection status
  const [carrierStatus, setCarrierStatus] = useState(null); // { connected, carrierId, carrierName, lastSync }
  const [matchedCarrier, setMatchedCarrier] = useState(null); // carrier from API that matches client's carrier
  const [showCarrierDisconnectConfirm, setShowCarrierDisconnectConfirm] = useState(false);

  // Connect modal
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [modalStep, setModalStep] = useState('method'); // 'choose' | 'method'
  const [selectedSource, setSelectedSource] = useState(null); // 'bluebutton' | carrier object
  const [directConnect, setDirectConnect] = useState(false); // true when opened from direct buttons (skip choice step)
  const [error, setError] = useState(null);
  const [sendingLink, setSendingLink] = useState(null);
  const [linkSent, setLinkSent] = useState(null);

  const carrierInfo = getCarrierInfo(clientCarrier);

  // Add medication modal
  const [showAddMedication, setShowAddMedication] = useState(false);

  // Data states
  const [activeTab, setActiveTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [prescriptions, setPrescriptions] = useState([]);
  const [outpatient, setOutpatient] = useState([]);
  const [dme, setDme] = useState([]);
  const [snf, setSnf] = useState([]);
  const [hospice, setHospice] = useState([]);
  const [inpatient, setInpatient] = useState([]);
  const [ambulance, setAmbulance] = useState([]);
  const [providers, setProviders] = useState([]);
  const [costAnalytics, setCostAnalytics] = useState(null);
  const [coverage, setCoverage] = useState([]);
  const [changes, setChanges] = useState([]);
  const [conditions, setConditions] = useState([]);

  useEffect(() => {
    loadStatus();
  }, [clientId]);

  const loadStatus = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load Blue Button status
      const bbResult = await getBlueButtonStatus(clientId);
      setStatus(bbResult);

      // Load carrier connection status and matched carrier
      try {
        // Get all carrier connections for this client
        const connections = await getClientCarrierConnections(clientId);
        if (connections?.connections?.length > 0) {
          const conn = connections.connections[0]; // Use first/most recent
          const tokenAge = conn.token_expires_at ? (Date.now() - new Date(conn.token_expires_at).getTime()) : Infinity;
          const isStale = tokenAge > 24 * 60 * 60 * 1000; // >24h = likely expired
          setCarrierStatus({
            connected: !isStale,
            stale: isStale,
            carrierId: conn.carrier_id,
            carrierName: conn.carrierName || CARRIER_INFO[conn.carrier_id]?.name || conn.carrier_id,
            lastSync: conn.last_sync_at
          });
        } else {
          setCarrierStatus({ connected: false });
        }

        // Get matched carrier for this client's carrier
        if (clientCarrier) {
          const matched = await matchPatientAccessCarrier(clientCarrier);
          if (matched?.carrier) {
            setMatchedCarrier(matched.carrier);
          }
        }
      } catch (carrierErr) {
        console.warn('Failed to load carrier status:', carrierErr);
        setCarrierStatus({ connected: false });
      }

      // Always load data - includes manual medications even if not connected
      await loadAllData();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAllData = async () => {
    const API_BASE = process.env.REACT_APP_API_URL || '';
    try {
      const [
        summaryData,
        claimsData,
        changesData,
        outpatientData,
        dmeData,
        snfData,
        hospiceData,
        inpatientData,
        providersData,
        costData,
        coverageData,
        medicationsData,
        conditionsData,
        carrierCoverageData,
        careTeamData,
        carrierEncountersData,
        carrierClaimsData,
        carrierProceduresData
      ] = await Promise.all([
        getHealthDataSummary(clientId).catch(() => null),
        getBlueButtonClaims(clientId, 50).catch(() => ({ claims: [] })),
        getBlueButtonChanges(clientId, 20).catch(() => ({ changes: [] })),
        getOutpatientClaims(clientId, 50).catch(() => ({ claims: [] })),
        getDMEClaims(clientId, 20).catch(() => ({ claims: [] })),
        getSNFStays(clientId).catch(() => ({ stays: [] })),
        getHospiceEpisodes(clientId).catch(() => ({ episodes: [] })),
        getMedicalEvents(clientId, 50).catch(() => ({ events: [] })),
        getClientProviders(clientId).catch(() => ({ providers: [] })),
        getCostAnalytics(clientId).catch(() => null),
        getCoveragePeriods(clientId).catch(() => ({ periods: [] })),
        // Fetch medications from database (includes manual + Blue Button + carrier data)
        fetch(`${API_BASE}/medications/client/${clientId}`, { credentials: 'include' })
          .then(res => res.json())
          .catch(() => ({ medications: [] })),
        // Fetch conditions from Patient Access data
        fetch(`${API_BASE}/health-data/conditions/${clientId}`, { credentials: 'include' })
          .then(res => res.ok ? res.json() : { conditions: [] })
          .catch(() => ({ conditions: [] })),
        // Fetch coverage from Patient Access (carrier data with benefits)
        fetch(`${API_BASE}/health-data/coverage/${clientId}`, { credentials: 'include' })
          .then(res => res.ok ? res.json() : { coverage: [] })
          .catch(() => ({ coverage: [] })),
        // Fetch care team from Patient Access
        fetch(`${API_BASE}/health-data/care-team/${clientId}`, { credentials: 'include' })
          .then(res => res.ok ? res.json() : { careTeam: [] })
          .catch(() => ({ careTeam: [] })),
        // Fetch encounters from Patient Access (carrier data)
        fetch(`${API_BASE}/health-data/encounters/${clientId}`, { credentials: 'include' })
          .then(res => res.ok ? res.json() : { encounters: [] })
          .catch(() => ({ encounters: [] })),
        // Fetch claims from Patient Access (carrier data)
        fetch(`${API_BASE}/health-data/claims/${clientId}`, { credentials: 'include' })
          .then(res => res.ok ? res.json() : { claims: [] })
          .catch(() => ({ claims: [] })),
        // Fetch procedures from Patient Access (carrier data)
        fetch(`${API_BASE}/health-data/procedures/${clientId}`, { credentials: 'include' })
          .then(res => res.ok ? res.json() : { procedures: [] })
          .catch(() => ({ procedures: [] }))
      ]);

      setSummary(summaryData);

      // Merge Blue Button claims with database medications
      const bbClaims = claimsData.claims || [];
      const dbMeds = medicationsData.medications || [];

      // Convert database medications to prescription format for display
      const dbMedsAsPrescriptions = dbMeds.map(med => ({
        id: `db-${med.id}`,
        drug_name: med.medication_name,
        rxnorm_code: med.rxnorm_code,
        ndc_code: med.ndc_code,
        dosage: med.dosage,
        quantity: med.quantity,
        frequency: med.frequency,
        prescriber: med.prescriber,
        formulary_tier: med.formulary_tier,
        prior_auth_required: med.prior_auth_required,
        step_therapy_required: med.step_therapy_required,
        quantity_limit: med.quantity_limit,
        cost_sharing: med.cost_sharing,
        selected_pharmacy_type: med.selected_pharmacy_type || 'retail',
        source: med.source || 'manual',
        carrier_id: med.carrier_id,
        fill_date: med.last_fill_date || med.humana_last_fill || med.bluebutton_last_fill || med.created_at,
        last_fill_date: med.last_fill_date,
        first_fill_date: med.first_fill_date,
        fill_count: med.fill_count,
        fill_frequency_days: med.fill_frequency_days,
        actual_copay_avg: med.actual_copay_avg,
        is_active: med.is_active,
        notes: med.notes,
        // New fields from Patient Access API
        is_prescribed: med.is_prescribed,
        is_filled: med.is_filled,
        times_per_day: med.times_per_day,
        days_supply: med.days_supply,
        authored_date: med.authored_date,
        status: med.status,
        _dbId: med.id // Keep reference to database ID for editing
      }));

      // Combine and deduplicate (prefer database records as they have more fields)
      const allPrescriptions = [...dbMedsAsPrescriptions];

      // Add Blue Button claims that aren't already in DB
      for (const claim of bbClaims) {
        const exists = dbMedsAsPrescriptions.some(
          m => m.rxnorm_code === claim.rxnorm_code ||
               m.drug_name?.toLowerCase() === claim.drug_name?.toLowerCase()
        );
        if (!exists) {
          allPrescriptions.push(claim);
        }
      }

      setPrescriptions(allPrescriptions);
      setChanges(changesData.changes || []);

      // Merge carrier encounters with Blue Button data by type
      const carrierEncs = carrierEncountersData.encounters || [];
      const carrierProcs = carrierProceduresData.procedures || [];

      // Convert carrier encounters to display format (match Blue Button field names)
      const formatCarrierEncounter = (enc) => {
        const providers = typeof enc.providers === 'string' ? JSON.parse(enc.providers || '[]') : (enc.providers || []);
        const procedures = carrierProcs.filter(p => p.encounter_fhir_id === enc.fhir_id);
        return {
          id: `carrier-${enc.id}`,
          fhir_id: enc.fhir_id,
          // Map to Blue Button expected field names
          service_date: enc.admission_date,
          cpt_description: enc.encounter_name,
          claim_type: enc.encounter_type,
          event_type: enc.encounter_type, // For inpatient filtering compatibility
          provider_name: providers[0]?.name || null,
          facility_name: enc.facility_name,
          // Additional carrier-specific fields
          admission_date: enc.admission_date,
          discharge_date: enc.discharge_date,
          reason: enc.reason,
          providers: providers,
          diagnosis: typeof enc.diagnosis === 'string' ? JSON.parse(enc.diagnosis || '[]') : (enc.diagnosis || []),
          status: enc.status,
          procedures: procedures,
          source: 'patient_access',
          carrier_id: enc.carrier_id
        };
      };

      // Split carrier encounters by type
      const carrierInpatient = carrierEncs.filter(e => e.encounter_type === 'inpatient').map(formatCarrierEncounter);
      const carrierOutpatient = carrierEncs.filter(e => e.encounter_type === 'outpatient' || e.encounter_type === 'other').map(formatCarrierEncounter);
      const carrierSnf = carrierEncs.filter(e => e.encounter_type === 'snf').map(formatCarrierEncounter);
      const carrierHospice = carrierEncs.filter(e => e.encounter_type === 'hospice').map(formatCarrierEncounter);
      const carrierEr = carrierEncs.filter(e => e.encounter_type === 'er').map(formatCarrierEncounter);
      const carrierDme = carrierEncs.filter(e => e.encounter_type === 'dme').map(formatCarrierEncounter);
      const carrierAmbulance = carrierEncs.filter(e => e.encounter_type === 'ambulance').map(formatCarrierEncounter);

      // Merge with Blue Button data
      setOutpatient([...(outpatientData.claims || []), ...carrierOutpatient, ...carrierEr]);
      setAmbulance([...carrierAmbulance]);
      setDme([...(dmeData.claims || []), ...carrierDme]);
      setSnf([...(snfData.stays || []), ...carrierSnf]);
      setHospice([...(hospiceData.episodes || []), ...carrierHospice]);
      setInpatient([...(inpatientData.events || []), ...carrierInpatient]);

      // Merge Blue Button providers with Patient Access care team
      const bbProviders = providersData.providers || [];
      const careTeam = careTeamData.careTeam || [];
      const mergedProviders = [...bbProviders];
      for (const member of careTeam) {
        const exists = bbProviders.some(p =>
          p.npi === member.provider_npi ||
          (p.name && member.provider_name && p.name.toLowerCase() === member.provider_name.toLowerCase())
        );
        if (!exists) {
          mergedProviders.push({
            id: member.id,
            name: member.provider_name,
            npi: member.provider_npi,
            specialty: member.specialty,
            provider_type: member.role || 'provider',
            phone: member.phone,
            address: member.address,
            source: 'patient_access'
          });
        }
      }
      setProviders(mergedProviders);

      setCostAnalytics(costData);

      // Merge Blue Button coverage with Patient Access carrier coverage
      const bbCoverage = coverageData.periods || [];
      const carrierCoverage = carrierCoverageData.coverage || [];
      const mergedCoverage = [...bbCoverage];
      for (const cc of carrierCoverage) {
        // Add carrier coverage entries (they have benefits info)
        mergedCoverage.push({
          id: cc.id,
          coverage_type: cc.coverage_type,
          plan_name: cc.plan_name,
          plan_id: cc.plan_id,
          carrier: cc.carrier_name || cc.payor,
          start_date: cc.effective_start,
          end_date: cc.effective_end,
          subscriber_id: cc.subscriber_id,
          group_name: cc.group_name,
          relationship: cc.relationship,
          network: cc.network,
          benefits: cc.benefits, // IMPORTANT: Benefits info for Coverage tab
          source: 'patient_access',
          carrier_id: cc.carrier_id
        });
      }
      setCoverage(mergedCoverage);

      setConditions(conditionsData.conditions || []);
    } catch (err) {
      console.error('Error loading health data:', err);
    }
  };

  // Connect Blue Button - goes directly to SMS/Email choice
  const handleConnectBlueButton = () => {
    setSelectedSource('bluebutton');
    setDirectConnect(true);
    setShowConnectModal(true);
    setModalStep('method');
    setLinkSent(null);
    setError(null);
  };

  // Connect Carrier - goes directly to SMS/Email choice
  const handleConnectCarrier = (carrier) => {
    setSelectedSource(carrier); // carrier object with id, name, etc.
    setDirectConnect(true);
    setShowConnectModal(true);
    setModalStep('method');
    setLinkSent(null);
    setError(null);
  };

  // Full modal flow (used by header "Connect Health Data" button if needed)
  const handleConnectClick = () => {
    setDirectConnect(false);
    setShowConnectModal(true);
    setModalStep('choose');
    setSelectedSource(null);
    setLinkSent(null);
    setError(null);
  };

  const handleSelectSource = (source) => {
    setSelectedSource(source);
    setModalStep('method');
  };

  const handleBackToChoice = () => {
    setModalStep('choose');
    setSelectedSource(null);
    setError(null);
  };

  const handleSendEmail = async () => {
    try {
      setSendingLink('email');
      setError(null);

      let result;
      if (selectedSource === 'bluebutton') {
        result = await sendBlueButtonLinkEmail(clientId);
      } else if (selectedSource?.id) {
        // It's a carrier object
        result = await sendCarrierLinkEmail(selectedSource.id, clientId);
      } else {
        throw new Error('No source selected');
      }

      if (result.ok) {
        setLinkSent('email');
      } else {
        setError(result.error || 'Failed to send email');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSendingLink(null);
    }
  };

  const handleSendSMS = async () => {
    try {
      setSendingLink('sms');
      setError(null);

      let result;
      if (selectedSource === 'bluebutton') {
        result = await sendBlueButtonLinkSMS(clientId);
      } else if (selectedSource?.id) {
        // It's a carrier object
        result = await sendCarrierLinkSMS(selectedSource.id, clientId);
      } else {
        throw new Error('No source selected');
      }

      if (result.ok) {
        setLinkSent('sms');
      } else {
        setError(result.error || 'Failed to send SMS');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSendingLink(null);
    }
  };

  const handleDisconnect = async () => {
    try {
      setDisconnecting(true);
      setError(null);
      await disconnectBlueButton(clientId);
      setShowDisconnectConfirm(false);
      // Clear all data
      setSummary(null);
      setPrescriptions([]);
      setChanges([]);
      setOutpatient([]);
      setDme([]);
      setSnf([]);
      setHospice([]);
      setInpatient([]);
      setProviders([]);
      setCostAnalytics(null);
      setCoverage([]);
      await loadStatus();
    } catch (err) {
      setError(err.message);
    } finally {
      setDisconnecting(false);
    }
  };

  const handleCarrierDisconnect = async () => {
    if (!carrierStatus?.carrierId) return;
    try {
      setDisconnecting(true);
      setError(null);
      await disconnectCarrier(carrierStatus.carrierId, clientId);
      setShowCarrierDisconnectConfirm(false);
      setCarrierStatus({ connected: false });
      await loadAllData(); // Reload data
    } catch (err) {
      setError(err.message);
    } finally {
      setDisconnecting(false);
    }
  };

  const handleCarrierSync = async () => {
    if (!carrierStatus?.carrierId) return;
    try {
      setCarrierSyncing(true);
      setError(null);
      await triggerCarrierSync(carrierStatus.carrierId, clientId);
      // Re-classify encounters based on encounter name
      try {
        await reclassifyEncounters(clientId);
      } catch (reclassifyErr) {
        console.warn('Reclassify not available:', reclassifyErr.message);
      }
      await loadAllData();
      alert(`${carrierStatus.carrierName} sync complete!`);
    } catch (err) {
      setError(err.message);
    } finally {
      setCarrierSyncing(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      const result = await syncBlueButtonData(clientId);
      await loadAllData();
      alert(`Sync complete! Found ${result.newClaims} new claims.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // ALWAYS SHOW FULL HEALTH DATA INTERFACE
  // ============================================================================
  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Medicare Health Data</h3>
              <p className="text-sm text-gray-500">
                {status?.connected
                  ? `Last synced: ${status.lastSync ? formatDate(status.lastSync) : 'Never'}`
                  : 'Manage medications and health information'
                }
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Blue Button Status/Connect */}
            {status?.connected ? (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  Blue Button Connected
                </span>
                <button
                  onClick={handleSync}
                  disabled={syncing}
                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                  title="Sync Blue Button"
                >
                  <svg className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                <button
                  onClick={() => setShowDisconnectConfirm(true)}
                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Disconnect Blue Button"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <button
                onClick={() => handleConnectBlueButton()}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-blue-200"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                Connect Blue Button
              </button>
            )}

            {/* Carrier Status/Connect - only show if client has a carrier and it's available */}
            {(matchedCarrier || carrierStatus?.connected) && (
              <>
                <div className="w-px h-6 bg-gray-300"></div>
                {carrierStatus?.connected ? (
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-green-100 text-green-800 border border-green-200`}>
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      {carrierStatus.carrierName} Connected
                    </span>
                    <button
                      onClick={handleCarrierSync}
                      disabled={carrierSyncing}
                      className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                      title={`Sync ${carrierStatus.carrierName}`}
                    >
                      <svg className={`w-4 h-4 ${carrierSyncing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setShowCarrierDisconnectConfirm(true)}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title={`Disconnect ${carrierStatus.carrierName}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : matchedCarrier ? (
                  <button
                    onClick={() => handleConnectCarrier(matchedCarrier)}
                    className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg transition-colors border ${
                      matchedCarrier.color === 'bg-purple-600' ? 'text-purple-600 border-purple-200 hover:bg-purple-50' :
                      matchedCarrier.color === 'bg-green-500' ? 'text-green-600 border-green-200 hover:bg-green-50' :
                      matchedCarrier.color === 'bg-blue-600' ? 'text-blue-600 border-blue-200 hover:bg-blue-50' :
                      matchedCarrier.color === 'bg-orange-500' ? 'text-orange-600 border-orange-200 hover:bg-orange-50' :
                      'text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {matchedCarrier.logo && (
                      <img src={matchedCarrier.logo} alt={matchedCarrier.name} className="w-4 h-4 mr-1.5 object-contain" />
                    )}
                    Connect {matchedCarrier.name}
                  </button>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Connect Modal */}
      {showConnectModal && (
        <ConnectModal
          clientName={clientName}
          clientEmail={clientEmail}
          clientPhone={clientPhone}
          carrierInfo={carrierInfo}
          modalStep={modalStep}
          selectedSource={selectedSource}
          error={error}
          sendingLink={sendingLink}
          linkSent={linkSent}
          showBackButton={!directConnect}
          onClose={() => setShowConnectModal(false)}
          onSelectSource={handleSelectSource}
          onBackToChoice={directConnect ? null : handleBackToChoice}
          onSendEmail={handleSendEmail}
          onSendSMS={handleSendSMS}
        />
      )}

      {error && (
        <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="px-6 pt-4 border-b border-gray-200">
        <div className="flex gap-1 overflow-x-auto">
          <Tab active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>Overview</Tab>
          <Tab active={activeTab === 'prescriptions'} onClick={() => setActiveTab('prescriptions')} count={prescriptions.length}>Prescriptions</Tab>
          <Tab active={activeTab === 'medical'} onClick={() => setActiveTab('medical')} count={inpatient.length + outpatient.length + ambulance.length}>Medical</Tab>
          <Tab active={activeTab === 'coverage'} onClick={() => setActiveTab('coverage')} count={coverage.length}>Coverage</Tab>
          <Tab active={activeTab === 'providers'} onClick={() => setActiveTab('providers')} count={providers.length}>Providers</Tab>
          <Tab active={activeTab === 'costs'} onClick={() => setActiveTab('costs')}>Costs</Tab>
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'overview' && (
          <OverviewTab
            summary={summary}
            changes={changes}
            costAnalytics={costAnalytics}
            prescriptions={prescriptions}
            inpatient={inpatient}
          />
        )}
        {activeTab === 'prescriptions' && (
          <PrescriptionsTab
            prescriptions={prescriptions}
            changes={changes}
            onAddMedication={() => setShowAddMedication(true)}
          />
        )}
        {activeTab === 'medical' && (
          <MedicalTab
            inpatient={inpatient}
            outpatient={outpatient}
            snf={snf}
            hospice={hospice}
            dme={dme}
            ambulance={ambulance}
            conditions={conditions}
          />
        )}
        {activeTab === 'coverage' && (
          <CoverageTab coverage={coverage} summary={summary} />
        )}
        {activeTab === 'providers' && (
          <ProvidersTab providers={providers} />
        )}
        {activeTab === 'costs' && (
          <CostsTab
            costAnalytics={costAnalytics}
            inpatient={inpatient}
            outpatient={outpatient}
            snf={snf}
            dme={dme}
          />
        )}
      </div>

      {/* Disconnect Blue Button Modal */}
      {showDisconnectConfirm && (
        <DisconnectModal
          title="Disconnect Blue Button"
          message="Are you sure you want to disconnect Medicare Blue Button? This will remove access to claims data from Medicare.gov."
          disconnecting={disconnecting}
          error={error}
          onClose={() => {
            setShowDisconnectConfirm(false);
            setError(null);
          }}
          onConfirm={handleDisconnect}
        />
      )}

      {/* Disconnect Carrier Modal */}
      {showCarrierDisconnectConfirm && (
        <DisconnectModal
          title={`Disconnect ${carrierStatus?.carrierName || 'Carrier'}`}
          message={`Are you sure you want to disconnect ${carrierStatus?.carrierName || 'this carrier'}? This will remove access to health data from the carrier's Patient Access API.`}
          disconnecting={disconnecting}
          error={error}
          onClose={() => {
            setShowCarrierDisconnectConfirm(false);
            setError(null);
          }}
          onConfirm={handleCarrierDisconnect}
        />
      )}

      {/* Add Medication Modal */}
      <AddMedicationModal
        isOpen={showAddMedication}
        onClose={() => setShowAddMedication(false)}
        clientId={clientId}
        onSave={(medication, formularyData) => {
          // Add the new medication to the prescriptions list with formulary data
          setPrescriptions(prev => [
            {
              id: medication.id,
              drug_name: medication.medication_name,
              rxnorm_code: medication.rxnorm_code,
              ndc_code: medication.ndc_code,
              dosage: medication.dosage,
              quantity: medication.quantity,
              frequency: medication.frequency,
              prescriber: medication.prescriber,
              source: 'manual',
              fill_date: new Date().toISOString(),
              // Formulary data
              formulary_tier: medication.formulary_tier || formularyData?.tier,
              formulary_tier_number: formularyData?.tierNumber,
              prior_auth_required: medication.prior_auth_required || formularyData?.priorAuthRequired,
              step_therapy_required: medication.step_therapy_required || formularyData?.stepTherapyRequired,
              quantity_limit: medication.quantity_limit || formularyData?.quantityLimit,
              cost_sharing: formularyData?.costSharing,
              selected_pharmacy_type: formularyData?.selectedPharmacyType || 'retail'
            },
            ...prev
          ]);
        }}
      />
    </div>
  );
}

// ============================================================================
// TAB COMPONENTS
// ============================================================================

function OverviewTab({ summary, changes, costAnalytics, prescriptions, inpatient }) {
  return (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-blue-700">
            {prescriptions.length}
          </div>
          <div className="text-sm text-blue-600">Prescriptions</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-purple-700">
            {costAnalytics?.unique_drugs || 0}
          </div>
          <div className="text-sm text-purple-600">Unique Medications</div>
        </div>
        <div className="bg-orange-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-orange-700">
            {inpatient.filter(e => e.event_type === 'inpatient').length}
          </div>
          <div className="text-sm text-orange-600">Hospitalizations</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-700">
            {formatCurrency(costAnalytics?.ytd_oop_spend_cents)}
          </div>
          <div className="text-sm text-green-600">YTD Out-of-Pocket</div>
        </div>
      </div>

      {/* Recent Changes/Alerts */}
      {changes.length > 0 && (
        <div>
          <SectionHeader title="Recent Changes" subtitle="Medication and treatment changes that may affect retention" />
          <div className="space-y-2">
            {changes.slice(0, 5).map((change) => (
              <div
                key={change.id}
                className={`p-3 rounded-lg border ${
                  change.reviewed_at ? 'bg-gray-50 border-gray-200' : 'bg-yellow-50 border-yellow-200'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      change.change_type === 'new_medication' ? 'bg-blue-100 text-blue-800' :
                      change.change_type === 'discontinued' ? 'bg-red-100 text-red-800' :
                      change.change_type === 'new_prescriber' ? 'bg-purple-100 text-purple-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {change.change_type.replace(/_/g, ' ')}
                    </span>
                    <p className="mt-1 text-sm font-medium text-gray-900">{change.drug_name}</p>
                    <p className="text-xs text-gray-500">{formatDate(change.detected_at)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Coverage Summary */}
      {summary && (
        <div>
          <SectionHeader title="Coverage Summary" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {summary.has_part_a && (
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span className="text-sm">Part A (Hospital)</span>
              </div>
            )}
            {summary.has_part_b && (
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span className="text-sm">Part B (Medical)</span>
              </div>
            )}
            {summary.has_part_c && (
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span className="text-sm">Part C (MA)</span>
              </div>
            )}
            {summary.has_part_d && (
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span className="text-sm">Part D (Rx)</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PrescriptionsTab({ prescriptions, changes, onAddMedication }) {
  // Show add button even when no prescriptions
  const AddButton = () => (
    <button
      onClick={onAddMedication}
      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
      Add Medication
    </button>
  );

  if (prescriptions.length === 0) {
    return (
      <div className="text-center py-8">
        <svg className="w-12 h-12 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p className="text-gray-500 text-sm mb-4">No prescription claims found</p>
        {onAddMedication && <AddButton />}
      </div>
    );
  }

  // Always show formulary columns so user can see the structure
  const hasFormularyData = true; // Always show tier column
  const hasRestrictionData = true; // Always show alerts column
  const hasCopayData = true; // Always show copay column

  // Get tier display info
  const getTierBadge = (rx) => {
    const tier = rx.formulary_tier || rx.drug_tier;
    if (!tier) return null;

    const tierStr = String(tier);
    const tierNum = rx.formulary_tier_number || (tierStr.match(/\d/) ? parseInt(tierStr.match(/\d/)[0]) : null);

    // Color code by tier (lower is better/cheaper)
    const tierColors = {
      1: 'bg-green-100 text-green-800',
      2: 'bg-blue-100 text-blue-800',
      3: 'bg-yellow-100 text-yellow-800',
      4: 'bg-orange-100 text-orange-800',
      5: 'bg-red-100 text-red-800'
    };

    return (
      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${tierColors[tierNum] || 'bg-gray-100 text-gray-800'}`}>
        {tier}
      </span>
    );
  };

  // Get copay display - supports both formulary copay and actual_copay_avg
  const getCopayDisplay = (rx) => {
    // First check for actual copay from claims data
    if (rx.actual_copay_avg !== undefined && rx.actual_copay_avg !== null) {
      return (
        <span className="text-sm font-medium text-green-700">
          ${Number(rx.actual_copay_avg).toFixed(2)}
        </span>
      );
    }

    // Fall back to formulary cost_sharing
    if (!rx.cost_sharing || rx.cost_sharing.length === 0) return null;

    // Get the selected pharmacy type, default to retail
    const selectedType = rx.selected_pharmacy_type || 'retail';

    // Find matching cost entry based on selected pharmacy type
    let costEntry = null;
    if (selectedType === 'mail_order') {
      costEntry = rx.cost_sharing.find(c => c.pharmacyType?.toLowerCase().includes('mail'));
    } else if (selectedType === 'preferred') {
      costEntry = rx.cost_sharing.find(c => c.pharmacyType?.toLowerCase().includes('preferred'));
    } else {
      // retail (default)
      costEntry = rx.cost_sharing.find(c => c.pharmacyType?.toLowerCase().includes('retail') && !c.pharmacyType?.toLowerCase().includes('preferred'));
    }

    // Fallback to first entry if no match
    if (!costEntry) {
      costEntry = rx.cost_sharing[0];
    }

    if (costEntry.copay !== undefined && costEntry.copay !== null) {
      return (
        <span className="text-sm font-medium text-green-700">
          ${costEntry.copay.toFixed(2)}
        </span>
      );
    }
    if (costEntry.coinsurance !== undefined && costEntry.coinsurance !== null) {
      return (
        <span className="text-sm font-medium text-green-700">
          {(costEntry.coinsurance * 100).toFixed(0)}%
        </span>
      );
    }
    return null;
  };

  // Get fill frequency display
  const getFillInfo = (rx) => {
    if (!rx.fill_count && !rx.fill_frequency_days) return null;

    let fillText = '';

    // Show fill frequency (Monthly, Every 30 days, etc.)
    if (rx.fill_frequency_days) {
      if (rx.fill_frequency_days >= 28 && rx.fill_frequency_days <= 31) {
        fillText = 'Monthly';
      } else if (rx.fill_frequency_days >= 84 && rx.fill_frequency_days <= 93) {
        fillText = 'Quarterly';
      } else {
        fillText = `Every ${rx.fill_frequency_days}d`;
      }
    }

    // Add fill count
    if (rx.fill_count && rx.fill_count > 0) {
      fillText += fillText ? ` • ${rx.fill_count} fills` : `${rx.fill_count} fills`;
    }

    return fillText || null;
  };

  // Get restriction badges
  const getRestrictionBadges = (rx) => {
    const badges = [];
    if (rx.prior_auth_required) {
      badges.push(
        <span key="pa" className="inline-flex px-1.5 py-0.5 text-xs rounded bg-purple-100 text-purple-800" title="Prior Authorization Required">
          PA
        </span>
      );
    }
    if (rx.step_therapy_required) {
      badges.push(
        <span key="st" className="inline-flex px-1.5 py-0.5 text-xs rounded bg-indigo-100 text-indigo-800" title="Step Therapy Required">
          ST
        </span>
      );
    }
    if (rx.quantity_limit) {
      badges.push(
        <span key="ql" className="inline-flex px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-800" title={`Quantity Limit: ${rx.quantity_limit}`}>
          QL
        </span>
      );
    }
    return badges.length > 0 ? <div className="flex gap-1">{badges}</div> : <span className="text-xs text-gray-400">-</span>;
  };

  return (
    <div className="space-y-4">
      {/* Header with Add button */}
      {onAddMedication && (
        <div className="flex justify-end">
          <AddButton />
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Drug</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
              {hasFormularyData && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
              )}
              {hasCopayData && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Copay</th>
              )}
              {hasRestrictionData && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Alerts</th>
              )}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {prescriptions.map((rx) => (
              <tr key={rx.id} className={`hover:bg-gray-50 ${rx.is_active === false ? 'opacity-50' : ''}`}>
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">{rx.drug_name}</div>
                  {rx.generic_name && rx.generic_name !== rx.drug_name && (
                    <div className="text-xs text-gray-500">{rx.generic_name}</div>
                  )}
                  {rx.dosage && !rx.dosage.toLowerCase().includes('qualifier') && !rx.dosage.toLowerCase().includes('as needed') && (
                    <div className="text-xs text-blue-600">{rx.dosage}</div>
                  )}
                  {rx.ndc_code && <div className="text-xs text-gray-400">NDC: {rx.ndc_code}</div>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1">
                    {rx.is_prescribed && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-800">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Prescribed
                      </span>
                    )}
                    {rx.is_filled && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-green-100 text-green-800">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Filled
                      </span>
                    )}
                    {!rx.is_prescribed && !rx.is_filled && (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {/* Show times_per_day if available, otherwise show cleaned frequency */}
                  {rx.times_per_day ? (
                    <div className="text-xs text-purple-600">{rx.times_per_day}x daily</div>
                  ) : rx.frequency && !rx.frequency.includes('qualifier value') ? (
                    <div className="text-xs">{rx.frequency}</div>
                  ) : null}
                  {getFillInfo(rx) && (
                    <div className="text-xs text-blue-600 font-medium">{getFillInfo(rx)}</div>
                  )}
                  {rx.prescriber && (
                    <div className="text-xs text-gray-400">
                      {rx.prescriber.startsWith('Dr.') ? rx.prescriber : `Dr. ${rx.prescriber}`}
                    </div>
                  )}
                  {(rx.last_fill_date || rx.fill_date) && (
                    <div className="text-xs text-gray-400">
                      Last filled: {formatDate(rx.last_fill_date || rx.fill_date)}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">{rx.quantity || rx.days_supply ? `${rx.quantity || ''} ${rx.days_supply ? `(${rx.days_supply}d)` : ''}`.trim() : '-'}</td>
                {hasFormularyData && (
                  <td className="px-4 py-3">
                    {getTierBadge(rx) || <span className="text-xs text-gray-400">-</span>}
                  </td>
                )}
                {hasCopayData && (
                  <td className="px-4 py-3">
                    {getCopayDisplay(rx) || <span className="text-xs text-gray-400">-</span>}
                  </td>
                )}
                {hasRestrictionData && (
                  <td className="px-4 py-3">
                    {getRestrictionBadges(rx)}
                  </td>
                )}
                <td className="px-4 py-3">
                  <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                    rx.source === 'manual' ? 'bg-purple-100 text-purple-800' :
                    rx.source === 'bluebutton' ? 'bg-blue-100 text-blue-800' :
                    rx.source === 'humana' ? 'bg-green-100 text-green-800' :
                    rx.source === 'cigna' ? 'bg-orange-100 text-orange-800' :
                    rx.source === 'patient_access' ? 'bg-purple-100 text-purple-800' :
                    rx.source === 'aetna' ? 'bg-purple-100 text-purple-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {rx.source === 'manual' ? 'Manual' :
                     rx.source === 'bluebutton' ? 'Medicare' :
                     rx.source === 'humana' ? 'Humana' :
                     rx.source === 'cigna' ? 'Cigna' :
                     rx.source === 'patient_access' ? (rx.carrier_id ? rx.carrier_id.charAt(0).toUpperCase() + rx.carrier_id.slice(1) : 'Carrier') :
                     rx.source === 'aetna' ? 'Aetna' :
                     rx.source || 'Unknown'}
                  </span>
                  {rx.is_active === false && (
                    <span className="ml-1 inline-flex px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">
                      Inactive
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ConditionsList({ conditions }) {
  if (!conditions || conditions.length === 0) {
    return <EmptyState message="No health conditions found" />;
  }

  // Separate active vs resolved conditions
  const activeConditions = conditions.filter(c => c.clinical_status === 'active');
  const resolvedConditions = conditions.filter(c => c.clinical_status !== 'active');

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      resolved: 'bg-gray-100 text-gray-600',
      inactive: 'bg-gray-100 text-gray-600',
      recurrence: 'bg-yellow-100 text-yellow-800',
      relapse: 'bg-orange-100 text-orange-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="space-y-4">
      {/* Active Conditions */}
      {activeConditions.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Active Conditions ({activeConditions.length})</h4>
          <div className="space-y-2">
            {activeConditions.map((condition) => (
              <div key={condition.id} className="border rounded-lg p-3 bg-white hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium text-gray-900">{condition.condition_name}</div>
                    {condition.onset_date && (
                      <div className="text-xs text-gray-500">Since: {formatDate(condition.onset_date)}</div>
                    )}
                    {condition.category && (
                      <div className="text-xs text-gray-400 capitalize">{condition.category.replace('-', ' ')}</div>
                    )}
                  </div>
                  <span className={`inline-flex px-2 py-1 text-xs rounded-full ${getStatusBadge(condition.clinical_status)}`}>
                    {condition.clinical_status || 'Unknown'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resolved Conditions */}
      {resolvedConditions.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-500 mb-2">Resolved/Past Conditions ({resolvedConditions.length})</h4>
          <div className="space-y-2 opacity-75">
            {resolvedConditions.map((condition) => (
              <div key={condition.id} className="border rounded-lg p-3 bg-gray-50">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium text-gray-700">{condition.condition_name}</div>
                    {condition.onset_date && (
                      <div className="text-xs text-gray-500">
                        {condition.recorded_date ? `${formatDate(condition.onset_date)} - ${formatDate(condition.recorded_date)}` : `Since: ${formatDate(condition.onset_date)}`}
                      </div>
                    )}
                  </div>
                  <span className={`inline-flex px-2 py-1 text-xs rounded-full ${getStatusBadge(condition.clinical_status)}`}>
                    {condition.clinical_status || 'Resolved'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MedicalTab({ inpatient, outpatient, snf, hospice, dme, ambulance = [], conditions = [] }) {
  const [subTab, setSubTab] = useState('conditions');

  // Count active conditions
  const activeConditions = conditions.filter(c => c.clinical_status === 'active');

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setSubTab('conditions')}
          className={`px-3 py-1.5 text-sm rounded-lg ${subTab === 'conditions' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
        >
          Conditions ({conditions.length})
        </button>
        <button
          onClick={() => setSubTab('inpatient')}
          className={`px-3 py-1.5 text-sm rounded-lg ${subTab === 'inpatient' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
        >
          Inpatient ({inpatient.filter(e => e.event_type === 'inpatient').length})
        </button>
        <button
          onClick={() => setSubTab('outpatient')}
          className={`px-3 py-1.5 text-sm rounded-lg ${subTab === 'outpatient' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
        >
          Outpatient ({outpatient.length})
        </button>
        <button
          onClick={() => setSubTab('er')}
          className={`px-3 py-1.5 text-sm rounded-lg ${subTab === 'er' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
        >
          ER Visits ({outpatient.filter(c => c.claim_type === 'emergency' || c.claim_type === 'er').length})
        </button>
        <button
          onClick={() => setSubTab('snf')}
          className={`px-3 py-1.5 text-sm rounded-lg ${subTab === 'snf' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
        >
          SNF ({snf.length})
        </button>
        <button
          onClick={() => setSubTab('hospice')}
          className={`px-3 py-1.5 text-sm rounded-lg ${subTab === 'hospice' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
        >
          Hospice ({hospice.length})
        </button>
        <button
          onClick={() => setSubTab('dme')}
          className={`px-3 py-1.5 text-sm rounded-lg ${subTab === 'dme' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
        >
          DME ({dme.length})
        </button>
        <button
          onClick={() => setSubTab('ambulance')}
          className={`px-3 py-1.5 text-sm rounded-lg ${subTab === 'ambulance' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
        >
          Ambulance ({ambulance.length})
        </button>
      </div>

      {subTab === 'conditions' && (
        <ConditionsList conditions={conditions} />
      )}
      {subTab === 'inpatient' && (
        <InpatientList events={inpatient.filter(e => e.event_type === 'inpatient')} />
      )}
      {subTab === 'outpatient' && (
        <OutpatientList claims={outpatient.filter(c => c.claim_type !== 'emergency' && c.claim_type !== 'er')} />
      )}
      {subTab === 'er' && (
        <OutpatientList claims={outpatient.filter(c => c.claim_type === 'emergency' || c.claim_type === 'er')} title="ER Visits" />
      )}
      {subTab === 'snf' && (
        <SNFList stays={snf} />
      )}
      {subTab === 'hospice' && (
        <HospiceList episodes={hospice} />
      )}
      {subTab === 'dme' && (
        <DMEList claims={dme} />
      )}
      {subTab === 'ambulance' && (
        <AmbulanceList transports={ambulance} />
      )}
    </div>
  );
}

function AmbulanceList({ transports }) {
  const [expandedId, setExpandedId] = useState(null);

  if (transports.length === 0) return <EmptyState message="No ambulance transports found" />;

  return (
    <div className="space-y-2">
      {transports.map((transport) => {
        const isExpanded = expandedId === transport.id;
        return (
          <div key={transport.id} className="border rounded-lg overflow-hidden">
            <button
              onClick={() => setExpandedId(isExpanded ? null : transport.id)}
              className="w-full p-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                  <span className="text-red-600 text-sm">🚑</span>
                </div>
                <div>
                  <span className="font-medium text-gray-900">{transport.cpt_description || 'Ambulance Transport'}</span>
                  <div className="text-sm text-gray-500">
                    {transport.service_date ? new Date(transport.service_date).toLocaleDateString() : 'Date unknown'}
                    {transport.provider_name && ` • ${transport.provider_name}`}
                  </div>
                </div>
              </div>
              <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </button>
            {isExpanded && (
              <div className="px-4 pb-4 bg-gray-50">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {transport.facility_name && (
                    <div>
                      <span className="text-gray-500">Facility:</span>
                      <span className="ml-2 text-gray-900">{transport.facility_name}</span>
                    </div>
                  )}
                  {transport.reason && (
                    <div className="col-span-2">
                      <span className="text-gray-500">Reason:</span>
                      <span className="ml-2 text-gray-900">{transport.reason}</span>
                    </div>
                  )}
                  {transport.diagnosis?.length > 0 && (
                    <div className="col-span-2">
                      <span className="text-gray-500">Diagnosis:</span>
                      <ul className="ml-2 text-gray-900">
                        {transport.diagnosis.map((dx, i) => (
                          <li key={i}>{dx.display || dx.code}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function InpatientList({ events }) {
  const [expandedId, setExpandedId] = useState(null);

  if (events.length === 0) return <EmptyState message="No hospital admissions found" />;

  return (
    <div className="space-y-2">
      {events.map((event) => {
        const isExpanded = expandedId === event.id;
        return (
          <div key={event.id} className="border rounded-lg overflow-hidden">
            {/* Summary Row - Always Visible */}
            <button
              onClick={() => setExpandedId(isExpanded ? null : event.id)}
              className="w-full p-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${event.discharge_date ? 'bg-gray-400' : 'bg-green-500'}`} />
                <div>
                  <span className="font-medium text-gray-900">{event.description || 'Hospital Admission'}</span>
                  <span className="text-gray-400 mx-2">•</span>
                  <span className="text-gray-600">{formatDate(event.admission_date)}</span>
                </div>
              </div>
              <svg className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Expanded Details */}
            {isExpanded && (
              <div className="px-4 pb-4 pt-2 bg-gray-50 border-t">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500">Facility</label>
                    <div className="text-sm font-medium">{event.facility_name || 'Unknown'}</div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Duration</label>
                    <div className="text-sm font-medium">
                      {event.admission_date && event.discharge_date
                        ? `${formatDate(event.admission_date)} - ${formatDate(event.discharge_date)}`
                        : formatDate(event.admission_date)}
                      {event.length_of_stay && ` (${event.length_of_stay} days)`}
                    </div>
                  </div>
                  {event.primary_diagnosis_desc && (
                    <div className="col-span-2">
                      <label className="text-xs text-gray-500">Primary Diagnosis</label>
                      <div className="text-sm font-medium">{event.primary_diagnosis_desc}</div>
                    </div>
                  )}
                  {event.medicare_paid_cents && (
                    <div>
                      <label className="text-xs text-gray-500">Medicare Paid</label>
                      <div className="text-sm font-medium text-green-700">{formatCurrency(event.medicare_paid_cents)}</div>
                    </div>
                  )}
                  {event.beneficiary_paid_cents && (
                    <div>
                      <label className="text-xs text-gray-500">Your Responsibility</label>
                      <div className="text-sm font-medium text-orange-700">{formatCurrency(event.beneficiary_paid_cents)}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function OutpatientList({ claims, title = "Outpatient Services" }) {
  const [expandedId, setExpandedId] = useState(null);

  if (claims.length === 0) return <EmptyState message={`No ${title.toLowerCase()} found`} />;

  return (
    <div className="space-y-2">
      {claims.map((claim) => {
        const isExpanded = expandedId === claim.id;
        const serviceName = claim.cpt_description || claim.hcpcs_description || claim.claim_type || 'Service';

        return (
          <div key={claim.id} className="border rounded-lg overflow-hidden">
            {/* Summary Row - Always Visible */}
            <button
              onClick={() => setExpandedId(isExpanded ? null : claim.id)}
              className="w-full p-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-blue-400" />
                <div>
                  <span className="font-medium text-gray-900">{serviceName}</span>
                  <span className="text-gray-400 mx-2">•</span>
                  <span className="text-gray-600">{formatDate(claim.service_date)}</span>
                </div>
              </div>
              <svg className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Expanded Details */}
            {isExpanded && (
              <div className="px-4 pb-4 pt-2 bg-gray-50 border-t">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500">Service Date</label>
                    <div className="text-sm font-medium">{formatDate(claim.service_date)}</div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Provider</label>
                    <div className="text-sm font-medium">{claim.provider_name || claim.facility_name || 'Unknown'}</div>
                  </div>
                  {claim.cpt_code && (
                    <div>
                      <label className="text-xs text-gray-500">CPT Code</label>
                      <div className="text-sm font-medium">{claim.cpt_code}</div>
                    </div>
                  )}
                  {claim.hcpcs_code && (
                    <div>
                      <label className="text-xs text-gray-500">HCPCS Code</label>
                      <div className="text-sm font-medium">{claim.hcpcs_code}</div>
                    </div>
                  )}
                  {claim.medicare_paid_cents && (
                    <div>
                      <label className="text-xs text-gray-500">Medicare Paid</label>
                      <div className="text-sm font-medium text-green-700">{formatCurrency(claim.medicare_paid_cents)}</div>
                    </div>
                  )}
                  {claim.beneficiary_paid_cents && (
                    <div>
                      <label className="text-xs text-gray-500">Your Responsibility</label>
                      <div className="text-sm font-medium text-orange-700">{formatCurrency(claim.beneficiary_paid_cents)}</div>
                    </div>
                  )}
                  {claim.facility_name && claim.provider_name && (
                    <div className="col-span-2">
                      <label className="text-xs text-gray-500">Facility</label>
                      <div className="text-sm font-medium">{claim.facility_name}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SNFList({ stays }) {
  const [expandedId, setExpandedId] = useState(null);

  if (stays.length === 0) return <EmptyState message="No SNF stays found" />;

  return (
    <div className="space-y-2">
      {stays.map((stay) => {
        const isExpanded = expandedId === stay.id;
        return (
          <div key={stay.id} className="border rounded-lg overflow-hidden">
            {/* Summary Row */}
            <button
              onClick={() => setExpandedId(isExpanded ? null : stay.id)}
              className="w-full p-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${stay.discharge_date ? 'bg-gray-400' : 'bg-green-500'}`} />
                <div>
                  <span className="font-medium text-gray-900">Skilled Nursing Facility</span>
                  <span className="text-gray-400 mx-2">•</span>
                  <span className="text-gray-600">{formatDate(stay.admission_date)}</span>
                </div>
              </div>
              <svg className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Expanded Details */}
            {isExpanded && (
              <div className="px-4 pb-4 pt-2 bg-gray-50 border-t">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500">Facility</label>
                    <div className="text-sm font-medium">{stay.facility_name || 'Unknown'}</div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Duration</label>
                    <div className="text-sm font-medium">
                      {stay.admission_date && stay.discharge_date
                        ? `${formatDate(stay.admission_date)} - ${formatDate(stay.discharge_date)}`
                        : `Started ${formatDate(stay.admission_date)}`}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Total Days</label>
                    <div className="text-sm font-medium">{stay.length_of_stay || 0} days</div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Covered Days</label>
                    <div className="text-sm font-medium text-green-700">{stay.covered_days || 0} days</div>
                  </div>
                  {stay.medicare_paid_cents && (
                    <div>
                      <label className="text-xs text-gray-500">Medicare Paid</label>
                      <div className="text-sm font-medium text-green-700">{formatCurrency(stay.medicare_paid_cents)}</div>
                    </div>
                  )}
                  {stay.beneficiary_paid_cents && (
                    <div>
                      <label className="text-xs text-gray-500">Your Responsibility</label>
                      <div className="text-sm font-medium text-orange-700">{formatCurrency(stay.beneficiary_paid_cents)}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function HospiceList({ episodes }) {
  const [expandedId, setExpandedId] = useState(null);

  if (episodes.length === 0) return <EmptyState message="No hospice episodes found" />;

  return (
    <div className="space-y-2">
      {episodes.map((ep) => {
        const isExpanded = expandedId === ep.id;
        const isActive = !ep.end_date;

        return (
          <div key={ep.id} className="border rounded-lg overflow-hidden">
            {/* Summary Row */}
            <button
              onClick={() => setExpandedId(isExpanded ? null : ep.id)}
              className="w-full p-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-red-500' : 'bg-gray-400'}`} />
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">Hospice Care</span>
                  {isActive && (
                    <span className="inline-flex px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded-full">Active</span>
                  )}
                  <span className="text-gray-400 mx-1">•</span>
                  <span className="text-gray-600">{formatDate(ep.start_date)}</span>
                </div>
              </div>
              <svg className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Expanded Details */}
            {isExpanded && (
              <div className="px-4 pb-4 pt-2 bg-gray-50 border-t">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500">Provider</label>
                    <div className="text-sm font-medium">{ep.hospice_name || 'Unknown'}</div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Status</label>
                    <div className="text-sm font-medium">{isActive ? 'Active' : 'Ended'}</div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Start Date</label>
                    <div className="text-sm font-medium">{formatDate(ep.start_date)}</div>
                  </div>
                  {ep.end_date && (
                    <div>
                      <label className="text-xs text-gray-500">End Date</label>
                      <div className="text-sm font-medium">{formatDate(ep.end_date)}</div>
                    </div>
                  )}
                  {ep.medicare_paid_cents && (
                    <div>
                      <label className="text-xs text-gray-500">Medicare Paid</label>
                      <div className="text-sm font-medium text-green-700">{formatCurrency(ep.medicare_paid_cents)}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DMEList({ claims }) {
  const [expandedId, setExpandedId] = useState(null);

  if (claims.length === 0) return <EmptyState message="No DME claims found" />;

  return (
    <div className="space-y-2">
      {claims.map((claim) => {
        const isExpanded = expandedId === claim.id;
        const equipmentName = claim.hcpcs_description || claim.hcpcs_code || 'Equipment';
        const isRental = claim.rental_indicator;

        return (
          <div key={claim.id} className="border rounded-lg overflow-hidden">
            {/* Summary Row */}
            <button
              onClick={() => setExpandedId(isExpanded ? null : claim.id)}
              className="w-full p-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${isRental ? 'bg-blue-400' : 'bg-green-400'}`} />
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">{equipmentName}</span>
                  <span className={`inline-flex px-2 py-0.5 text-xs rounded-full ${isRental ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                    {isRental ? 'Rental' : 'Purchase'}
                  </span>
                  <span className="text-gray-400 mx-1">•</span>
                  <span className="text-gray-600">{formatDate(claim.service_date)}</span>
                </div>
              </div>
              <svg className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Expanded Details */}
            {isExpanded && (
              <div className="px-4 pb-4 pt-2 bg-gray-50 border-t">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500">Service Date</label>
                    <div className="text-sm font-medium">{formatDate(claim.service_date)}</div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Type</label>
                    <div className="text-sm font-medium">{isRental ? 'Rental' : 'Purchase'}</div>
                  </div>
                  {claim.supplier_name && (
                    <div>
                      <label className="text-xs text-gray-500">Supplier</label>
                      <div className="text-sm font-medium">{claim.supplier_name}</div>
                    </div>
                  )}
                  {claim.hcpcs_code && (
                    <div>
                      <label className="text-xs text-gray-500">HCPCS Code</label>
                      <div className="text-sm font-medium">{claim.hcpcs_code}</div>
                    </div>
                  )}
                  {claim.medicare_paid_cents && (
                    <div>
                      <label className="text-xs text-gray-500">Medicare Paid</label>
                      <div className="text-sm font-medium text-green-700">{formatCurrency(claim.medicare_paid_cents)}</div>
                    </div>
                  )}
                  {claim.beneficiary_paid_cents && (
                    <div>
                      <label className="text-xs text-gray-500">Your Responsibility</label>
                      <div className="text-sm font-medium text-orange-700">{formatCurrency(claim.beneficiary_paid_cents)}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CoverageTab({ coverage, summary }) {
  const [expandedId, setExpandedId] = useState(null);

  // Separate active coverage from historical
  const activeCoverage = coverage.filter(c => !c.end_date);
  const historicalCoverage = coverage.filter(c => c.end_date);

  // Get coverage type badge color
  const getCoverageTypeBadge = (coverageType) => {
    if (!coverageType) return 'bg-gray-100 text-gray-800';
    const typeMap = {
      'part_a': 'bg-blue-100 text-blue-800',
      'part_b': 'bg-green-100 text-green-800',
      'part_c': 'bg-purple-100 text-purple-800',
      'part_d': 'bg-orange-100 text-orange-800',
      'mapd': 'bg-purple-100 text-purple-800',
      'pdp': 'bg-orange-100 text-orange-800',
      'medical': 'bg-blue-100 text-blue-800',
      'pharmacy': 'bg-orange-100 text-orange-800'
    };
    const normalized = coverageType.toLowerCase().replace(/\s+/g, '_');
    return typeMap[normalized] || 'bg-gray-100 text-gray-800';
  };

  // Render a coverage item (expandable)
  const renderCoverageItem = (period) => {
    const isExpanded = expandedId === period.id;
    const hasBenefits = period.benefits && Object.keys(period.benefits).length > 0;

    return (
      <div key={period.id} className="border rounded-lg overflow-hidden">
        {/* Summary Row */}
        <button
          onClick={() => setExpandedId(isExpanded ? null : period.id)}
          className="w-full p-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
        >
          <div className="flex items-center gap-3">
            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${getCoverageTypeBadge(period.coverage_type)}`}>
              {(period.coverage_type || 'Coverage').replace(/_/g, ' ').toUpperCase()}
            </span>
            <div>
              <span className="font-medium text-gray-900">{period.plan_name || period.carrier || 'Plan'}</span>
              {!period.end_date && (
                <span className="ml-2 inline-flex px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">Active</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasBenefits && (
              <span className="text-xs text-blue-600">Benefits available</span>
            )}
            <svg className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>

        {/* Expanded Details */}
        {isExpanded && (
          <div className="px-4 pb-4 pt-2 bg-gray-50 border-t">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs text-gray-500">Plan Name</label>
                <div className="text-sm font-medium">{period.plan_name || '-'}</div>
              </div>
              <div>
                <label className="text-xs text-gray-500">Carrier/Payor</label>
                <div className="text-sm font-medium">{period.carrier || '-'}</div>
              </div>
              <div>
                <label className="text-xs text-gray-500">Effective Period</label>
                <div className="text-sm font-medium">
                  {formatDate(period.start_date)} {period.end_date ? `- ${formatDate(period.end_date)}` : '- Present'}
                </div>
              </div>
              {period.subscriber_id && (
                <div>
                  <label className="text-xs text-gray-500">Subscriber ID</label>
                  <div className="text-sm font-medium">{period.subscriber_id}</div>
                </div>
              )}
              {period.group_name && (
                <div>
                  <label className="text-xs text-gray-500">Group</label>
                  <div className="text-sm font-medium">{period.group_name}</div>
                </div>
              )}
              {period.network && (
                <div>
                  <label className="text-xs text-gray-500">Network</label>
                  <div className="text-sm font-medium">{period.network}</div>
                </div>
              )}
              {period.relationship && (
                <div>
                  <label className="text-xs text-gray-500">Relationship</label>
                  <div className="text-sm font-medium capitalize">{period.relationship}</div>
                </div>
              )}
            </div>

            {/* Benefits Summary - ONLY shown in Coverage tab */}
            {hasBenefits && (
              <div className="mt-4 pt-4 border-t">
                <h5 className="text-sm font-medium text-gray-900 mb-3">Benefits Summary</h5>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {period.benefits.deductible !== undefined && (
                    <div className="p-3 bg-white rounded-lg border">
                      <label className="text-xs text-gray-500">Deductible</label>
                      <div className="text-lg font-bold text-gray-900">
                        ${Number(period.benefits.deductible).toLocaleString()}
                      </div>
                    </div>
                  )}
                  {period.benefits.oop_max !== undefined && (
                    <div className="p-3 bg-white rounded-lg border">
                      <label className="text-xs text-gray-500">Out-of-Pocket Max</label>
                      <div className="text-lg font-bold text-gray-900">
                        ${Number(period.benefits.oop_max).toLocaleString()}
                      </div>
                    </div>
                  )}
                  {period.benefits.premium !== undefined && (
                    <div className="p-3 bg-white rounded-lg border">
                      <label className="text-xs text-gray-500">Monthly Premium</label>
                      <div className="text-lg font-bold text-gray-900">
                        ${Number(period.benefits.premium).toLocaleString()}
                      </div>
                    </div>
                  )}
                  {period.benefits.pcp_copay !== undefined && (
                    <div className="p-3 bg-white rounded-lg border">
                      <label className="text-xs text-gray-500">PCP Copay</label>
                      <div className="text-lg font-bold text-gray-900">
                        ${Number(period.benefits.pcp_copay).toLocaleString()}
                      </div>
                    </div>
                  )}
                  {period.benefits.specialist_copay !== undefined && (
                    <div className="p-3 bg-white rounded-lg border">
                      <label className="text-xs text-gray-500">Specialist Copay</label>
                      <div className="text-lg font-bold text-gray-900">
                        ${Number(period.benefits.specialist_copay).toLocaleString()}
                      </div>
                    </div>
                  )}
                  {period.benefits.er_copay !== undefined && (
                    <div className="p-3 bg-white rounded-lg border">
                      <label className="text-xs text-gray-500">ER Copay</label>
                      <div className="text-lg font-bold text-gray-900">
                        ${Number(period.benefits.er_copay).toLocaleString()}
                      </div>
                    </div>
                  )}
                  {period.benefits.inpatient_copay !== undefined && (
                    <div className="p-3 bg-white rounded-lg border">
                      <label className="text-xs text-gray-500">Inpatient Copay</label>
                      <div className="text-lg font-bold text-gray-900">
                        ${Number(period.benefits.inpatient_copay).toLocaleString()}/day
                      </div>
                    </div>
                  )}
                  {period.benefits.drug_deductible !== undefined && (
                    <div className="p-3 bg-white rounded-lg border">
                      <label className="text-xs text-gray-500">Drug Deductible</label>
                      <div className="text-lg font-bold text-gray-900">
                        ${Number(period.benefits.drug_deductible).toLocaleString()}
                      </div>
                    </div>
                  )}
                </div>

                {/* Additional benefits details if available */}
                {(period.benefits.dental || period.benefits.vision || period.benefits.hearing || period.benefits.otc || period.benefits.fitness) && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {period.benefits.dental && (
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Dental Included</span>
                    )}
                    {period.benefits.vision && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Vision Included</span>
                    )}
                    {period.benefits.hearing && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">Hearing Included</span>
                    )}
                    {period.benefits.otc && (
                      <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">OTC Allowance: ${period.benefits.otc}</span>
                    )}
                    {period.benefits.fitness && (
                      <span className="px-2 py-1 bg-teal-100 text-teal-800 text-xs rounded-full">Fitness Benefit</span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Demographics */}
      {summary && (
        <div>
          <SectionHeader title="Beneficiary Information" />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {summary.medicare_beneficiary_id && (
              <div>
                <label className="text-xs text-gray-500">Medicare ID (MBI)</label>
                <div className="text-sm font-medium">{summary.medicare_beneficiary_id}</div>
              </div>
            )}
            {summary.gender && (
              <div>
                <label className="text-xs text-gray-500">Gender</label>
                <div className="text-sm font-medium">{summary.gender}</div>
              </div>
            )}
            {summary.race && (
              <div>
                <label className="text-xs text-gray-500">Race</label>
                <div className="text-sm font-medium">{summary.race}</div>
              </div>
            )}
            {summary.ethnicity && (
              <div>
                <label className="text-xs text-gray-500">Ethnicity</label>
                <div className="text-sm font-medium">{summary.ethnicity}</div>
              </div>
            )}
            {summary.lis_level && (
              <div>
                <label className="text-xs text-gray-500">LIS Status</label>
                <div className="text-sm font-medium">{summary.lis_level}</div>
              </div>
            )}
            {summary.dual_eligible && (
              <div>
                <label className="text-xs text-gray-500">Dual Eligible</label>
                <div className="text-sm font-medium">Yes (Medicare + Medicaid)</div>
              </div>
            )}
            {summary.part_a_start && (
              <div>
                <label className="text-xs text-gray-500">Part A Start</label>
                <div className="text-sm font-medium">{formatDate(summary.part_a_start)}</div>
              </div>
            )}
            {summary.part_b_start && (
              <div>
                <label className="text-xs text-gray-500">Part B Start</label>
                <div className="text-sm font-medium">{formatDate(summary.part_b_start)}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Active Coverage */}
      {activeCoverage.length > 0 && (
        <div>
          <SectionHeader title="Current Coverage" subtitle={`${activeCoverage.length} active plan${activeCoverage.length !== 1 ? 's' : ''}`} />
          <div className="space-y-2">
            {activeCoverage.map(renderCoverageItem)}
          </div>
        </div>
      )}

      {/* Historical Coverage */}
      {historicalCoverage.length > 0 && (
        <div>
          <SectionHeader title="Coverage History" subtitle="Previous plans" />
          <div className="space-y-2">
            {historicalCoverage.map(renderCoverageItem)}
          </div>
        </div>
      )}

      {/* Empty State */}
      {coverage.length === 0 && !summary && (
        <EmptyState message="No coverage information found" />
      )}
    </div>
  );
}

function ProvidersTab({ providers }) {
  const [expandedId, setExpandedId] = useState(null);

  if (providers.length === 0) return <EmptyState message="No providers found" />;

  // Get network status badge
  const getNetworkBadge = (provider) => {
    if (provider.in_network === undefined || provider.in_network === null) {
      return null;
    }
    if (provider.in_network) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
          In-Network
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
        Out-of-Network
      </span>
    );
  };

  // Group by type
  const grouped = providers.reduce((acc, p) => {
    const type = p.provider_type || 'other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(p);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {Object.entries(grouped).map(([type, list]) => (
        <div key={type}>
          <SectionHeader title={type.charAt(0).toUpperCase() + type.slice(1) + 's'} />
          <div className="space-y-2">
            {list.map((provider) => {
              const isExpanded = expandedId === provider.id;

              return (
                <div key={provider.id} className="border rounded-lg overflow-hidden">
                  {/* Summary Row - Name + Role/Specialty */}
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : provider.id)}
                    className="w-full p-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-medium">
                        {(provider.name || 'U').charAt(0)}
                      </div>
                      <div>
                        <span className="font-medium text-gray-900">{provider.name || 'Unknown'}</span>
                        {provider.specialty && (
                          <>
                            <span className="text-gray-400 mx-2">•</span>
                            <span className="text-gray-600">{provider.specialty}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getNetworkBadge(provider)}
                      <svg className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-2 bg-gray-50 border-t">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs text-gray-500">Full Name</label>
                          <div className="text-sm font-medium">{provider.name || 'Unknown'}</div>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Type</label>
                          <div className="text-sm font-medium capitalize">{provider.provider_type || 'Provider'}</div>
                        </div>
                        {provider.specialty && (
                          <div>
                            <label className="text-xs text-gray-500">Specialty</label>
                            <div className="text-sm font-medium">{provider.specialty}</div>
                          </div>
                        )}
                        {provider.npi && (
                          <div>
                            <label className="text-xs text-gray-500">NPI</label>
                            <div className="text-sm font-medium">{provider.npi}</div>
                          </div>
                        )}
                        <div>
                          <label className="text-xs text-gray-500">Visits</label>
                          <div className="text-sm font-medium">{provider.interaction_count || 0} visit{(provider.interaction_count || 0) !== 1 ? 's' : ''}</div>
                        </div>
                        {provider.last_interaction_date && (
                          <div>
                            <label className="text-xs text-gray-500">Last Visit</label>
                            <div className="text-sm font-medium">{formatDate(provider.last_interaction_date)}</div>
                          </div>
                        )}
                        {provider.accepting_new_patients !== undefined && (
                          <div>
                            <label className="text-xs text-gray-500">Accepting Patients</label>
                            <div className={`text-sm font-medium ${provider.accepting_new_patients ? 'text-green-700' : 'text-orange-700'}`}>
                              {provider.accepting_new_patients ? 'Yes' : 'No'}
                            </div>
                          </div>
                        )}
                        {provider.address && (
                          <div className="col-span-2">
                            <label className="text-xs text-gray-500">Address</label>
                            <div className="text-sm font-medium">{provider.address}</div>
                          </div>
                        )}
                        {provider.phone && (
                          <div>
                            <label className="text-xs text-gray-500">Phone</label>
                            <div className="text-sm font-medium">{provider.phone}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function CostsTab({ costAnalytics, inpatient = [], outpatient = [], snf = [], dme = [] }) {
  const [expandedId, setExpandedId] = useState(null);
  const [costSubTab, setCostSubTab] = useState('summary');

  // Combine all medical claims (not pharmacy - those go to Prescriptions)
  const allMedicalClaims = [
    ...inpatient.map(e => ({
      ...e,
      eventType: 'inpatient',
      eventLabel: e.description || 'Hospital Admission',
      eventDate: e.admission_date,
      clientCost: e.beneficiary_paid_cents
    })),
    ...outpatient.map(c => ({
      ...c,
      eventType: c.claim_type === 'emergency' ? 'ER' : 'outpatient',
      eventLabel: c.cpt_description || c.hcpcs_description || c.claim_type || 'Service',
      eventDate: c.service_date,
      clientCost: c.beneficiary_paid_cents
    })),
    ...snf.map(s => ({
      ...s,
      eventType: 'snf',
      eventLabel: 'Skilled Nursing Facility',
      eventDate: s.admission_date,
      clientCost: s.beneficiary_paid_cents
    })),
    ...dme.map(d => ({
      ...d,
      eventType: 'dme',
      eventLabel: d.hcpcs_description || d.hcpcs_code || 'Equipment',
      eventDate: d.service_date,
      clientCost: d.beneficiary_paid_cents
    }))
  ].sort((a, b) => new Date(b.eventDate) - new Date(a.eventDate));

  const getEventTypeBadge = (eventType) => {
    const colors = {
      inpatient: 'bg-red-100 text-red-800',
      outpatient: 'bg-blue-100 text-blue-800',
      ER: 'bg-orange-100 text-orange-800',
      snf: 'bg-purple-100 text-purple-800',
      dme: 'bg-green-100 text-green-800'
    };
    return colors[eventType] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Sub-tabs for Summary vs Events */}
      <div className="flex gap-2">
        <button
          onClick={() => setCostSubTab('summary')}
          className={`px-3 py-1.5 text-sm rounded-lg ${costSubTab === 'summary' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
        >
          Summary
        </button>
        <button
          onClick={() => setCostSubTab('events')}
          className={`px-3 py-1.5 text-sm rounded-lg ${costSubTab === 'events' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
        >
          Events ({allMedicalClaims.length})
        </button>
      </div>

      {costSubTab === 'summary' ? (
        <>
          {/* Cost Summary */}
          {costAnalytics ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-xl font-bold text-blue-700">
                    {formatCurrency(costAnalytics.part_d_total_cost_cents)}
                  </div>
                  <div className="text-sm text-blue-600">Part D Total</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-xl font-bold text-green-700">
                    {formatCurrency(costAnalytics.part_d_beneficiary_paid_cents)}
                  </div>
                  <div className="text-sm text-green-600">Part D OOP</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="text-xl font-bold text-purple-700">
                    {formatCurrency(costAnalytics.part_a_medicare_paid_cents)}
                  </div>
                  <div className="text-sm text-purple-600">Part A Medicare Paid</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="text-xl font-bold text-orange-700">
                    {formatCurrency(costAnalytics.part_b_medicare_paid_cents)}
                  </div>
                  <div className="text-sm text-orange-600">Part B Medicare Paid</div>
                </div>
              </div>

              <div>
                <SectionHeader title="Utilization Summary" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3 border rounded-lg">
                    <div className="text-2xl font-bold text-gray-900">{costAnalytics.part_d_claim_count || 0}</div>
                    <div className="text-sm text-gray-500">Rx Fills</div>
                  </div>
                  <div className="p-3 border rounded-lg">
                    <div className="text-2xl font-bold text-gray-900">{costAnalytics.unique_drugs || 0}</div>
                    <div className="text-sm text-gray-500">Unique Drugs</div>
                  </div>
                  <div className="p-3 border rounded-lg">
                    <div className="text-2xl font-bold text-gray-900">{costAnalytics.er_visit_count || 0}</div>
                    <div className="text-sm text-gray-500">ER Visits</div>
                  </div>
                  <div className="p-3 border rounded-lg">
                    <div className="text-2xl font-bold text-gray-900">{costAnalytics.inpatient_days || 0}</div>
                    <div className="text-sm text-gray-500">Inpatient Days</div>
                  </div>
                </div>
              </div>

              {/* Utilization Flags */}
              <div className="flex flex-wrap gap-2">
                {costAnalytics.high_utilizer_flag && (
                  <span className="px-3 py-1 bg-red-100 text-red-800 text-sm rounded-full">High Utilizer</span>
                )}
                {costAnalytics.er_frequent_user_flag && (
                  <span className="px-3 py-1 bg-orange-100 text-orange-800 text-sm rounded-full">Frequent ER User</span>
                )}
                {costAnalytics.catastrophic_reached_flag && (
                  <span className="px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full">Catastrophic Phase</span>
                )}
              </div>
            </>
          ) : (
            <EmptyState message="No cost analytics available" />
          )}
        </>
      ) : (
        /* Events List - Expandable */
        <div className="space-y-2">
          {allMedicalClaims.length === 0 ? (
            <EmptyState message="No medical events found" />
          ) : (
            allMedicalClaims.map((event) => {
              const isExpanded = expandedId === event.id;

              return (
                <div key={event.id} className="border rounded-lg overflow-hidden">
                  {/* Summary Row - Event + Client Responsibility */}
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : event.id)}
                    className="w-full p-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex px-2 py-0.5 text-xs rounded-full ${getEventTypeBadge(event.eventType)}`}>
                        {event.eventType === 'ER' ? 'ER' : event.eventType.charAt(0).toUpperCase() + event.eventType.slice(1)}
                      </span>
                      <div>
                        <span className="font-medium text-gray-900">{event.eventLabel}</span>
                        <span className="text-gray-400 mx-2">•</span>
                        <span className="text-gray-600">{formatDate(event.eventDate)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {event.clientCost ? (
                        <span className="font-medium text-orange-700">
                          {formatCurrency(event.clientCost)}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">$0.00</span>
                      )}
                      <svg className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-2 bg-gray-50 border-t">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs text-gray-500">Event Type</label>
                          <div className="text-sm font-medium capitalize">{event.eventType}</div>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Date</label>
                          <div className="text-sm font-medium">{formatDate(event.eventDate)}</div>
                        </div>
                        {(event.facility_name || event.provider_name || event.supplier_name) && (
                          <div>
                            <label className="text-xs text-gray-500">Provider/Facility</label>
                            <div className="text-sm font-medium">{event.facility_name || event.provider_name || event.supplier_name}</div>
                          </div>
                        )}
                        {event.primary_diagnosis_desc && (
                          <div>
                            <label className="text-xs text-gray-500">Diagnosis</label>
                            <div className="text-sm font-medium">{event.primary_diagnosis_desc}</div>
                          </div>
                        )}
                        {event.length_of_stay && (
                          <div>
                            <label className="text-xs text-gray-500">Length of Stay</label>
                            <div className="text-sm font-medium">{event.length_of_stay} days</div>
                          </div>
                        )}
                        {event.medicare_paid_cents && (
                          <div>
                            <label className="text-xs text-gray-500">Medicare Paid</label>
                            <div className="text-sm font-medium text-green-700">{formatCurrency(event.medicare_paid_cents)}</div>
                          </div>
                        )}
                        <div>
                          <label className="text-xs text-gray-500">Your Responsibility</label>
                          <div className="text-sm font-medium text-orange-700">{formatCurrency(event.clientCost || 0)}</div>
                        </div>
                        {(event.cpt_code || event.hcpcs_code) && (
                          <div>
                            <label className="text-xs text-gray-500">Procedure Code</label>
                            <div className="text-sm font-medium">{event.cpt_code || event.hcpcs_code}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MODAL COMPONENTS
// ============================================================================

function ConnectModal({ clientName, clientEmail, clientPhone, carrierInfo, modalStep, selectedSource, error, sendingLink, linkSent, onClose, onSelectSource, onBackToChoice, onSendEmail, onSendSMS, showBackButton = true }) {
  // Determine the display name for the selected source
  const getSourceDisplayName = () => {
    if (selectedSource === 'bluebutton') return 'Medicare Blue Button';
    if (selectedSource?.name) return selectedSource.name; // carrier object
    if (selectedSource === 'carrier' && carrierInfo?.name) return carrierInfo.name;
    return 'Carrier';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {modalStep === 'method' && showBackButton && onBackToChoice && (
              <button
                onClick={onBackToChoice}
                className="text-gray-400 hover:text-gray-600 -ml-1"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <h3 className="text-lg font-semibold text-gray-900">
              {modalStep === 'choose' ? 'Connect Health Data' : `Connect ${getSourceDisplayName()}`}
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {linkSent ? (
            /* Success Message */
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">Link Sent!</h4>
              <p className="text-gray-500 mb-4">
                {linkSent === 'email'
                  ? `Authorization link sent to ${clientEmail || "client's email"}`
                  : `Authorization link sent to ${clientPhone || "client's phone"}`
                }
              </p>
              <p className="text-sm text-gray-400">
                The client will receive instructions to connect their {getSourceDisplayName()} account.
              </p>
              <button onClick={onClose} className="mt-6 px-6 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200">
                Close
              </button>
            </div>
          ) : modalStep === 'choose' ? (
            /* Step 1: Choose Data Source */
            <>
              <p className="text-gray-500 mb-6 text-center">
                Choose where to get {clientName}'s health data
              </p>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-3">
                {/* Blue Button Option */}
                <button
                  onClick={() => onSelectSource('bluebutton')}
                  className="w-full p-4 rounded-xl border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition flex items-center gap-4"
                >
                  <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-semibold text-gray-900 text-lg">Medicare Blue Button</div>
                    <div className="text-sm text-gray-500">Connect via Medicare.gov</div>
                    <div className="text-xs text-blue-600 mt-1">All Medicare claims & prescription data</div>
                  </div>
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                {/* Carrier Option - Only show if carrier is set */}
                {carrierInfo && (
                  <button
                    onClick={() => onSelectSource('carrier')}
                    className="w-full p-4 rounded-xl border-2 border-gray-200 hover:border-green-300 hover:bg-green-50 transition flex items-center gap-4"
                  >
                    <div className={`w-14 h-14 ${carrierInfo.color} rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden`}>
                      {carrierInfo.logo ? (
                        <img
                          src={carrierInfo.logo}
                          alt={carrierInfo.name}
                          className="w-10 h-10 object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className={`w-full h-full flex items-center justify-center text-white font-bold text-xl ${carrierInfo.logo ? 'hidden' : ''}`}>
                        {carrierInfo.name.charAt(0)}
                      </div>
                    </div>
                    <div className="flex-1 text-left">
                      <div className="font-semibold text-gray-900 text-lg">{carrierInfo.name}</div>
                      <div className="text-sm text-gray-500">Connect via carrier portal</div>
                      <div className="text-xs text-green-600 mt-1">Plan-specific benefits & formulary</div>
                    </div>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}

                {!carrierInfo && (
                  <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 text-center">
                    <p className="text-sm text-gray-500">
                      No carrier set for this client. Add their carrier in the profile to enable carrier-specific data.
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Step 2: Connection Method Options */
            <>
              <p className="text-gray-500 mb-6 text-center">
                Choose how to send the authorization link to {clientName}
              </p>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-3">
                {/* Email Option */}
                <button
                  onClick={onSendEmail}
                  disabled={sendingLink || !clientEmail}
                  className={`w-full p-4 rounded-xl border-2 transition flex items-center gap-4 ${
                    clientEmail
                      ? 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                      : 'border-gray-100 bg-gray-50 cursor-not-allowed'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    clientEmail ? 'bg-blue-100' : 'bg-gray-200'
                  }`}>
                    {sendingLink === 'email' ? (
                      <svg className="animate-spin w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg className={`w-6 h-6 ${clientEmail ? 'text-blue-600' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <div className={`font-medium ${clientEmail ? 'text-gray-900' : 'text-gray-400'}`}>
                      Send via Email
                    </div>
                    <div className="text-sm text-gray-500">
                      {clientEmail || 'No email on file'}
                    </div>
                  </div>
                  {clientEmail && (
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </button>

                {/* SMS Option */}
                <button
                  onClick={onSendSMS}
                  disabled={sendingLink || !clientPhone}
                  className={`w-full p-4 rounded-xl border-2 transition flex items-center gap-4 ${
                    clientPhone
                      ? 'border-gray-200 hover:border-green-300 hover:bg-green-50'
                      : 'border-gray-100 bg-gray-50 cursor-not-allowed'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    clientPhone ? 'bg-green-100' : 'bg-gray-200'
                  }`}>
                    {sendingLink === 'sms' ? (
                      <svg className="animate-spin w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg className={`w-6 h-6 ${clientPhone ? 'text-green-600' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <div className={`font-medium ${clientPhone ? 'text-gray-900' : 'text-gray-400'}`}>
                      Send via Text Message
                    </div>
                    <div className="text-sm text-gray-500">
                      {clientPhone || 'No phone on file'}
                    </div>
                  </div>
                  {clientPhone && (
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function DisconnectModal({ title = 'Disconnect Health Data', message, disconnecting, error, onClose, onConfirm }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
        <div className="px-6 py-4 border-b border-red-200 bg-red-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-red-900">{title}</h3>
              <p className="text-sm text-red-700">This action cannot be undone</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {message && (
            <p className="text-gray-700 mb-4">{message}</p>
          )}
          <p className="text-gray-700 mb-4">
            Disconnecting will <strong>remove access</strong> to health data:</p>

          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <ul className="space-y-2 text-sm text-gray-600">
              {['All prescription claims and drug history',
                'Hospital admissions and medical events',
                'SNF stays and hospice episodes',
                'Outpatient visits and ER records',
                'DME claims and equipment history',
                'Provider directory and interactions',
                'Cost analytics and spending data',
                'Coverage periods and eligibility'].map((item, i) => (
                <li key={i} className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={disconnecting}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {disconnecting ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Deleting...
                </>
              ) : (
                'Delete All Health Data'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
